'use client';

import type { ComponentProps } from 'react';
import React from 'react';
import { cn, Link } from '@skin-seoul/design-system';
import type { ProductCardProps } from '../ProductCard.types';
import { ProductCardProvider } from '../ProductCardContext';
import ProductCardInfoName from '../components/ProductCardInfoName';
import ProductCardInfoRating from '../components/ProductCardInfoRating';
import { ProductCardInfoPrice } from '../components/ProductCardInfoPrice';
import { HorizontalProductCardContent } from './HorizontalProductCardContent';
import { HorizontalProductCardImage } from './HorizontalProductCardImage';
import ProductCardAddToCart from '../components/ProductCardAddToCart';
import { extractPathname } from '@skin-seoul/utils';

type HorizontalProductCardProps = ProductCardProps & ComponentProps<typeof Link>;

export const HorizontalProductCard = ({
  children,
  theme,
  onClick,
  ranking,
  orientation = 'horizontal',
  ...props
}: HorizontalProductCardProps) => {
  const { href, linkComponent, className, ...restProps } = props;
  const normalizedHref = extractPathname(href);
  const LinkComponent = linkComponent || Link;

  return (
    <ProductCardProvider
      value={{
        ...restProps,
        href,
        orientation,
        theme,
        ranking,
      }}
    >
      <LinkComponent
        className={cn(
          'flex w-full relative rounded-[8px] flex-row items-center cursor-pointer no-underline hover:no-underline',
          theme === 'dark' ? 'bg-generic-100' : 'bg-white',
          ranking ? 'h-[164px]' : 'h-[136px]',
          className,
        )}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        href={normalizedHref}
        {...restProps}
      >
        <>{children}</>
      </LinkComponent>
    </ProductCardProvider>
  );
};

HorizontalProductCard.Content = HorizontalProductCardContent;
HorizontalProductCard.Image = HorizontalProductCardImage;
HorizontalProductCard.InfoName = ProductCardInfoName;
HorizontalProductCard.InfoRating = ProductCardInfoRating;
HorizontalProductCard.InfoPrice = ProductCardInfoPrice;
HorizontalProductCard.AddToCart = ProductCardAddToCart;
